/**
 * Schemas Zod propios de la API de monitoreo.
 *
 * Zod en el borde tambien aplica hacia adentro: la UI valida cada respuesta
 * antes de usarla. Los schemas de las entidades vienen de `@amoble/contracts`;
 * aca solo se describen los sobres (paginacion, detalle compuesto, sesion) que
 * son propios de este REST.
 */
import { z } from 'zod';
import { ROLES } from '../auth/roles';
import {
  deadLetterSchema,
  messageAttemptSchema,
  messageLogEntrySchema,
  quarantineEntrySchema,
} from './contract-schemas';

/** Sobre estandar de listados paginados. */
export function paginatedSchema<T extends z.ZodTypeAny>(
  itemSchema: T,
): z.ZodObject<{
  items: z.ZodArray<T>;
  page: z.ZodNumber;
  pageSize: z.ZodNumber;
  total: z.ZodNumber;
}> {
  return z.object({
    items: z.array(itemSchema),
    page: z.number().int().nonnegative(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
  });
}

export const roleSchema = z.enum(ROLES);

export const sessionUserSchema = z.object({
  email: z.string().min(1),
  name: z.string(),
  picture: z.string().nullable(),
  role: roleSchema,
});

export const messageListSchema = paginatedSchema(messageLogEntrySchema);

export const messageDetailSchema = z.object({
  message: messageLogEntrySchema,
  attempts: z.array(messageAttemptSchema),
  deadLetter: deadLetterSchema.nullable(),
  quarantine: quarantineEntrySchema.nullable(),
});

export const messageTraceSchema = z.object({
  correlationId: z.string(),
  messages: z.array(messageLogEntrySchema),
});

export const deadLetterRowSchema = z.object({
  deadLetter: deadLetterSchema,
  message: messageLogEntrySchema.nullable(),
});

export const deadLetterListSchema = paginatedSchema(deadLetterRowSchema);

export const quarantineRowSchema = z.object({
  quarantine: quarantineEntrySchema,
  message: messageLogEntrySchema.nullable(),
});

export const quarantineListSchema = paginatedSchema(quarantineRowSchema);

/**
 * Cuerpo de error de la API (RFC 7807 con extras). Se parsea de forma tolerante:
 * un error mal formado no puede impedir que mostremos que hubo un error.
 */
export const problemDetailsSchema = z.object({
  title: z.string().optional(),
  detail: z.string().optional(),
  message: z.union([z.string(), z.array(z.string())]).optional(),
  error: z.string().optional(),
  correlationId: z.string().optional(),
});
